<script setup lang="ts">
import { computed } from 'vue';
import {
  useRoute,
  useRouter,
} from 'vue-router';

import type { IArticle } from '@modules/articles/types';
import type { FormSchema } from '@modules/forms/components/VForm/types';
import VBackButton from '@components/buttons/VBackButton/VBackButton.vue';
import VCard from '@components/card/VCard/VCard.vue';
import VLoader from '@components/loader/VLoader.vue';
import {
  ADMIN_ARTICLES_LIST_ROUTE_NAME,
  ARTICLES_LOCALE_ALL,
  TRANSLATION_GROUP_ADMIN_ARTICLES,
} from '@modules/articles/constants';
import { useArticlesGetQuery } from '@modules/articles/queries';
import {
  ADMIN_ARTICLES_EDIT_ROUTE_NAME,

} from '@modules/articles/submodules/edit/constants';
import { useArticleSaveMutation } from '@modules/articles/submodules/edit/mutations';
import VForm from '@modules/forms/components/VForm/VForm.vue';
import { EFormInputTypes } from '@modules/forms/enums';
import {
  useI18n,
  useLanguages,
} from '@modules/i18n/composables';
import { isString } from '@utils/guards';

const { getTranslation } = useI18n();
const route = useRoute();
const router = useRouter();
const isEdit = computed<boolean>(() => route.name === ADMIN_ARTICLES_EDIT_ROUTE_NAME);
const id = computed<number>(() => (
  isString(route.params.id) ? Number(route.params.id) : 0
));

const { data: article, isLoading: isLoadingArticle } = useArticlesGetQuery(id);
const isLoading = computed(() => (
  isEdit.value && isLoadingArticle.value
));

const {
  mutateAsync: saveArticle,
  isLoading: isLoadingSave,
  errors,
  clearError,
} = useArticleSaveMutation();

const { interfaceLanguages } = useLanguages();

const formSchema = computed<FormSchema<IArticle>>(() => ({
  locale: {
    type: EFormInputTypes.SELECT,
    required: true,
    options: [
      {
        value: ARTICLES_LOCALE_ALL,
        label: getTranslation(TRANSLATION_GROUP_ADMIN_ARTICLES, 'ALL_LANGUAGES').value,
      },
      ...interfaceLanguages.value,
    ],
    label: getTranslation(TRANSLATION_GROUP_ADMIN_ARTICLES, 'FORM_FOR_LANGUAGE').value,
    value: article.value?.locale,
  },
  title: {
    type: EFormInputTypes.TEXT,
    required: true,
    label: getTranslation(TRANSLATION_GROUP_ADMIN_ARTICLES, 'FORM_TITLE').value,
    value: article.value?.title,
  },
  body: {
    type: EFormInputTypes.TEXT_EDITOR,
    required: true,
    label: getTranslation(TRANSLATION_GROUP_ADMIN_ARTICLES, 'FORM_BODY').value,
    value: article.value?.body,
  },
}));

async function onSubmit(values: IArticle): Promise<void> {
  await saveArticle({
    ...(article.value ?? {}),
    ...values,
    locale: values.locale === ARTICLES_LOCALE_ALL ? undefined : values.locale,
  });

  if (!isEdit.value) {
    router.back({
      name: ADMIN_ARTICLES_LIST_ROUTE_NAME,
    });
  }
}
</script>

<template>
  <VCard v-data-qa-auto-id="'ArticlesEditPage'"
    type="form"
    :title="isEdit
      ? $getTranslation(TRANSLATION_GROUP_ADMIN_ARTICLES, 'HEADER_EDIT')
      : $getTranslation(TRANSLATION_GROUP_ADMIN_ARTICLES, 'HEADER_CREATE')"
  >
    <VLoader
      :loading="isLoading"
      min-height="200px"
    >
      <VForm
        :schema="formSchema"
        :submit-btn-label="$getTranslation(TRANSLATION_GROUP_ADMIN_ARTICLES, 'FORM_SAVE')"
        :is-loading="isLoadingSave"
        :custom-errors="errors"
        @interacted="clearError"
        @submit="onSubmit"
      >
        <template #secondaryBtn>
          <VBackButton
            :to="{
              name: ADMIN_ARTICLES_LIST_ROUTE_NAME,
            }"
            :label="$getTranslation(TRANSLATION_GROUP_ADMIN_ARTICLES, 'FORM_BACK')"
          />
        </template>
      </VForm>
    </VLoader>
  </VCard>
</template>
